import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {BrandLogo} from '@salesforce/retail-react-app/app/components/icons';
import {Box, Center, Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerOverlay, IconButton, Spinner, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {HamburgerToCloseIcon} from '@amblique/king-living/app/components/icons/hamburger-to-close-icon';
import {MegaMenuPanel} from '@amblique/king-living/app/components/mega-menu/mobile/mega-menu-panel';
import {MegaMenuSubcategoryPanel} from '@amblique/king-living/app/components/mega-menu/mobile/mega-menu-subcategory-panel';

export const MegaMenuMobile = ({
  isOpen,
  isLoading = false,
  menuItems = [],
  navigationMenu,
  onClose,
  onLogoClick,
  onMyAccountClick,
  onMyQuoteClick,
  ...props
}) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [viewportHeight, setViewportHeight] = useState('100vh');
  const throttleTimeoutRef = useRef(null);
  const lastHeightRef = useRef(null);

  const styles = useMultiStyleConfig('MegaMenu', {isMobile: true});

  // Get actual viewport height (handles Safari's dynamic address bar)
  const getViewportHeight = useCallback(() => {
    if (typeof window === 'undefined') return '100vh';

    return window.visualViewport ?
      `${window.visualViewport.height}px` :
      `${window.innerHeight}px`;
  }, []);

  // Handle Safari's dynamic address bar - calculate actual viewport height
  // Throttled to prevent excessive re-renders
  const updateViewportHeight = useCallback(() => {
    // Clear any pending throttle timeout
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
    }

    // Throttle updates to max once per 100ms to prevent excessive re-renders
    throttleTimeoutRef.current = setTimeout(() => {
      const newHeight = getViewportHeight();

      // Only update state if height actually changed (prevents unnecessary re-renders)
      if (lastHeightRef.current !== newHeight) {
        lastHeightRef.current = newHeight;
        setViewportHeight(newHeight);
      }
    }, 100);
  }, [getViewportHeight]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;

    // Set initial height immediately (no throttle for initial render)
    const initialHeight = getViewportHeight();

    lastHeightRef.current = initialHeight;
    setViewportHeight(initialHeight);

    // Update on resize (address bar show/hide triggers resize)
    const eventOptions = {passive: true};

    window.addEventListener('resize', updateViewportHeight, eventOptions);
    window.addEventListener('orientationchange', updateViewportHeight, eventOptions);

    // Visual Viewport API is more accurate for Safari's dynamic address bar
    let visualViewportCleanup;

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight, eventOptions);

      visualViewportCleanup = () => {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
      };
    }

    return () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }

      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);

      if (visualViewportCleanup) {
        visualViewportCleanup();
      }
    };
  }, [isOpen, updateViewportHeight, getViewportHeight]);

  const handleArrowClick = (menuItem) => {
    setSelectedCategory(menuItem);
  };

  const handleSubcategoryBack = () => {
    setSelectedCategory(null);
  };

  const handleClose = () => {
    setSelectedCategory(null);
    onClose();
  };

  const isSubcategoryPanelOpen = !!selectedCategory;

  return (
    <Drawer
      data-testid="mega-menu-drawer"
      isOpen={isOpen}
      placement="left"
      size="full"
      onClose={handleClose}
      {...props}
      {...styles.mobileDrawer}
    >
      <DrawerOverlay />
      <DrawerContent
        className="mega-menu-drawer-content"
        display="flex"
        flexDirection="column"
        {...styles.mobileDrawerContent}
        sx={{
          height: viewportHeight,
          maxHeight: viewportHeight,
          ...styles.mobileDrawerContent?.sx,
        }}
      >
        <DrawerHeader className="mega-menu-drawer-header" {...styles.mobileDrawerHeader}>
          <IconButton
            icon={<BrandLogo {...styles.logoMobile} />}
            variant="unstyled"
            onClick={onLogoClick || handleClose}
            {...styles.logoMobileWrapper}
          />
          <IconButton
            aria-label="Close menu"
            icon={<HamburgerToCloseIcon isOpen={isOpen} />}
            variant="unstyled"
            onClick={handleClose}
            {...styles.closeButton}
          />
        </DrawerHeader>
        <DrawerBody className="mega-menu-drawer-body" {...styles.mobileDrawerBody}>
          {isLoading ? (
            <Center flex={1} height="100%" width="100%">
              <Spinner color={styles.megaMenuLoadingSpinner?.color} size="md" />
            </Center>
          ) : (
            <Box height="100%" position="relative" width="100%" {...styles.mobileDrawerBodyInner}>
              <MegaMenuPanel
                menuItems={menuItems}
                showFunctionalSections={!isSubcategoryPanelOpen}
                transform={isSubcategoryPanelOpen ? 'translateX(-100%)' : 'translateX(0)'}
                zIndex={isSubcategoryPanelOpen ? 1 : 2}
                onArrowClick={handleArrowClick}
                onMyAccountClick={onMyAccountClick}
                onMyQuoteClick={onMyQuoteClick}
              />
              {selectedCategory && (
                <MegaMenuSubcategoryPanel
                  category={selectedCategory}
                  transform={isSubcategoryPanelOpen ? 'translateX(0)' : 'translateX(100%)'}
                  zIndex={3}
                  onBackClick={handleSubcategoryBack}
                />
              )}
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

MegaMenuMobile.displayName = 'MegaMenuMobile';
MegaMenuMobile.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  menuItems: PropTypes.array,
  navigationMenu: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onLogoClick: PropTypes.func,
  onMyAccountClick: PropTypes.func,
  onMyQuoteClick: PropTypes.func,
};
